import { Controller } from "@hotwired/stimulus"
import {
  base64UrlToArrayBuffer,
  decodePublicKeyCredentialCreationOptions,
  decodePublicKeyCredentialRequestOptions,
  encodePublicKeyCredential,
  generateAndWrapUek,
  generateAndWrapTekWithRaw,
  rewrapTekWithUekAndGetRaw,
  getOtkFromFragment,
  normalizeBufferSource,
  storeUek,
  storeTekWrapped
} from "../crypto"

export default class extends Controller {
  static targets = ["status"]
  static values = {
    registrationOptionsUrl: String,
    registrationUrl: String,
    authenticationOptionsUrl: String,
    authenticationUrl: String,
    uekOptionsUrl: String,
    uekUrl: String,
    invitationTekWrapped: String,
    messages: Object
  }

  connect() {
    if (this.hasStatusTarget) {
      this.statusTarget.setAttribute("aria-live", "polite")
      this.setStatus("")
    }
  }

  async register(event) {
    if (!window.PublicKeyCredential) {
      this.setStatus(this.message("unsupported"), "error")
      return
    }

    await this.withBusy(event?.currentTarget, async () => {
      this.setStatus("")

      try {
        const optionsResponse = await fetch(this.registrationOptionsUrlValue, {
          headers: { "Accept": "application/json" }
        })

        if (!optionsResponse.ok) {
          throw new Error(this.message("fetchOptionsFailed"))
        }

        const options = await optionsResponse.json()
        const prfSalt = options.prf_salt
        const publicKey = decodePublicKeyCredentialCreationOptions(options)

        // Add PRF extension with eval to get PRF output during creation
        publicKey.extensions = {
          ...publicKey.extensions,
          prf: prfSalt ? {
            eval: {
              first: base64UrlToArrayBuffer(prfSalt)
            }
          } : {}
        }

        const credential = await navigator.credentials.create({ publicKey })

        const extensionResults = credential.getClientExtensionResults?.() || {}
        const prfResult = extensionResults.prf?.results?.first

        if (!prfResult) {
          throw new Error(this.message("prfUnsupported"))
        }

        const payload = encodePublicKeyCredential(credential)
        const response = await fetch(this.registrationUrlValue, {
          method: "POST",
          headers: this.jsonHeaders(),
          body: JSON.stringify({ credential: payload })
        })

        const result = await this.parseJsonResponse(response)
        if (!response.ok) {
          throw new Error(result.error || this.responseErrorMessage(response, "registrationFailed"))
        }

        const submitResult = await this.submitUekAndTek(prfResult, prfSalt, result.tek_required)
        window.location.href = submitResult.redirect_to
      } catch (error) {
        this.setStatus(this.friendlyErrorMessage(error, "register"), "error")
      }
    })
  }

  async authenticate(event) {
    if (!window.PublicKeyCredential) {
      this.setStatus(this.message("unsupported"), "error")
      return
    }

    await this.withBusy(event?.currentTarget, async () => {
      this.setStatus("")

      try {
        let assertion = await this.createAuthenticationAssertion()

        if (!assertion.prfBytes) {
          assertion = await this.createAuthenticationAssertion()
        }

        const result = await this.submitAuthenticationAssertion(assertion)

        // Unwrap UEK with PRF from this authentication
        if (result.uek_wrapped_private_key) {
          if (!assertion.prfBytes) {
            throw new Error(this.message("prfUnsupported"))
          }

          await this.unwrapAndStoreUek(assertion.prfBytes, result.uek_wrapped_private_key)

          if (result.tek_wrapped) {
            await this.unwrapAndStoreTekWithStoredUek(result.tek_wrapped)
          }
        }

        window.location.href = result.redirect_to
      } catch (error) {
        this.setStatus(this.friendlyErrorMessage(error, "authenticate"), "error")
      }
    })
  }

  async createAuthenticationAssertion() {
    const optionsResponse = await fetch(this.authenticationOptionsUrlValue, {
      headers: { "Accept": "application/json" }
    })

    if (!optionsResponse.ok) {
      throw new Error(this.message("fetchOptionsFailed"))
    }

    // Options include PRF extension with a fixed salt, so the browser
    // evaluates PRF during this single tap.
    const options = await optionsResponse.json()
    const publicKey = decodePublicKeyCredentialRequestOptions(options)
    const credential = await navigator.credentials.get({ publicKey })

    const extensionResults = credential.getClientExtensionResults?.() || {}
    const prfResult = extensionResults.prf?.results?.first

    return {
      credential,
      prfBytes: prfResult ? normalizeBufferSource(prfResult, "Passkey PRF result") : null
    }
  }

  async submitAuthenticationAssertion(assertion) {
    const response = await fetch(this.authenticationUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ credential: encodePublicKeyCredential(assertion.credential) })
    })

    const result = await this.parseJsonResponse(response)
    if (!response.ok) {
      throw new Error(result.error || this.responseErrorMessage(response))
    }

    return result
  }

  setStatus(message, type = "info") {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.className = this.statusClass(type, message)
    }
  }

  statusClass(type, message) {
    if (!message) {
      return "hidden"
    }

    const variants = {
      error: "border-red-200 bg-red-50 text-red-700",
      success: "border-green-200 bg-green-50 text-green-700",
      info: "border-gray-200 bg-gray-50 text-gray-700"
    }

    return `mt-4 rounded-lg border px-3 py-2 text-sm leading-6 ${variants[type] || variants.info}`
  }

  async withBusy(button, callback) {
    if (button) {
      button.disabled = true
      button.setAttribute("aria-busy", "true")
    }

    try {
      await callback()
    } finally {
      if (button) {
        button.disabled = false
        button.removeAttribute("aria-busy")
      }
    }
  }

  friendlyErrorMessage(error, action) {
    const name = error?.name
    const message = error?.message || ""

    if (name === "NotAllowedError" || name === "AbortError" || name === "TimeoutError" || message.includes("operation either timed out or was not allowed")) {
      if (action === "authenticate") {
        return this.message("authenticateCanceled")
      }

      return this.message("actionCanceled")
    }

    if (name === "InvalidStateError") {
      return this.message("alreadyLinked")
    }

    if (name === "SecurityError") {
      return this.message("securityError")
    }

    if (name === "NotSupportedError") {
      return this.message("notSupported")
    }

    if (name === "TypeError" && message.includes("fetch")) {
      return this.message("networkError")
    }

    return message || this.message("unknownError")
  }

  responseErrorMessage(response, fallbackKey = "unknownError") {
    if (response.status === 401) {
      return this.message("unauthorized")
    }

    return this.message(fallbackKey)
  }

  async parseJsonResponse(response) {
    if (response.status === 204) {
      return {}
    }

    const text = await response.text()
    if (!text) {
      return {}
    }

    try {
      return JSON.parse(text)
    } catch {
      return {}
    }
  }

  message(key) {
    return this.messagesValue[key] || key
  }

  jsonHeaders() {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": token
    }
  }

  async setupUek(event) {
    if (!window.PublicKeyCredential) {
      this.setStatus(this.message("unsupported"), "error")
      return
    }

    await this.withBusy(event?.currentTarget, async () => {
      this.setStatus("")

      try {
        const optionsResponse = await fetch(this.uekOptionsUrlValue, {
          headers: { "Accept": "application/json" }
        })

        if (!optionsResponse.ok) {
          throw new Error(this.message("fetchOptionsFailed"))
        }

        const options = await optionsResponse.json()
        const publicKey = decodePublicKeyCredentialRequestOptions(options)
        const credential = await navigator.credentials.get({ publicKey })

        const extensionResults = credential.getClientExtensionResults?.() || {}
        const prfResult = extensionResults.prf?.results?.first
        if (!prfResult) {
          throw new Error(this.message("prfUnsupported"))
        }

        await this.submitUek(prfResult, options.extensions?.prf?.eval?.first, credential)
      } catch (error) {
        this.setStatus(this.friendlyErrorMessage(error, "setup"), "error")
      }
    })
  }

  async submitUekAndTek(prfResult, prfSalt, tekRequired, credential = null) {
    const { uekKeyPair, uekPrivateKeyDer, publicKeyDer, wrappedPrivateKey } = await generateAndWrapUek(prfResult)

    const body = {
      uek_public_key: publicKeyDer,
      uek_wrapped_private_key: wrappedPrivateKey,
      prf_salt: prfSalt
    }

    if (credential) {
      body.credential = encodePublicKeyCredential(credential)
    }

    // Handle TEK
    let tekRawForSession = null
    if (tekRequired) {
      // First user: generate new TEK key pair and wrap private key with UEK
      const { tekWrapped, tekPublicKey, tekRaw } = await generateAndWrapTekWithRaw(uekKeyPair.publicKey)
      body.tek_wrapped = tekWrapped
      body.tek_public_key = tekPublicKey
      tekRawForSession = tekRaw
    } else if (this.hasInvitationTekWrappedValue && this.invitationTekWrappedValue) {
      // Invited user: rewrap TEK from invitation with their UEK
      const otkFromFragment = getOtkFromFragment()
      if (!otkFromFragment) {
        throw new Error(this.message("invitationKeyMissing"))
      }
      const { tekWrapped, tekRaw } = await rewrapTekWithUekAndGetRaw(
        this.invitationTekWrappedValue,
        otkFromFragment,
        uekKeyPair.publicKey
      )
      body.tek_wrapped = tekWrapped
      tekRawForSession = tekRaw
    }

    const response = await fetch(this.uekUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify(body)
    })

    const result = await this.parseJsonResponse(response)
    if (!response.ok) {
      throw new Error(result.error || this.responseErrorMessage(response, "setupEncryptionFailed"))
    }

    // Store UEK private key as non-extractable CryptoKey in IndexedDB
    // (persists across tabs/restarts, cleared on logout via clearKeyStore)
    await storeUek(uekPrivateKeyDer)

    // Store wrapped TEK after successful server response (if available)
    if (body.tek_wrapped) {
      await storeTekWrapped(body.tek_wrapped)
    }

    return result
  }

  async submitUek(prfResult, prfSalt, credential = null) {
    const { publicKeyDer, wrappedPrivateKey } = await generateAndWrapUek(prfResult)

    const body = {
      uek_public_key: publicKeyDer,
      uek_wrapped_private_key: wrappedPrivateKey,
      prf_salt: prfSalt
    }

    if (credential) {
      body.credential = encodePublicKeyCredential(credential)
    }

    const response = await fetch(this.uekUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify(body)
    })

    const result = await this.parseJsonResponse(response)
    if (!response.ok) {
      throw new Error(result.error || this.responseErrorMessage(response, "setupEncryptionFailed"))
    }

    return result
  }

  async unwrapAndStoreTek(prfResult, uekWrappedPrivateKey, tekWrapped) {
    // First store UEK
    await this.unwrapAndStoreUek(prfResult, uekWrappedPrivateKey)
    // Then store wrapped TEK
    await storeTekWrapped(tekWrapped)
  }

  async unwrapAndStoreUek(prfResult, uekWrappedPrivateKey) {
    const prfBytes = normalizeBufferSource(prfResult, "Passkey PRF result")

    // Import PRF result as AES key
    const prfKey = await crypto.subtle.importKey(
      "raw",
      prfBytes,
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    )

    // Parse the wrapped UEK private key (format: iv || ciphertext)
    const combined = new Uint8Array(base64UrlToArrayBuffer(uekWrappedPrivateKey))
    const iv = combined.slice(0, 12)
    const ciphertext = combined.slice(12)

    // Decrypt the UEK private key
    const privateKeyDer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      prfKey,
      ciphertext
    )

    // Store UEK as non-extractable CryptoKey in IndexedDB
    await storeUek(privateKeyDer)
  }

  async unwrapAndStoreTekWithStoredUek(tekWrapped) {
    // Just store the wrapped TEK - it will be unwrapped on-demand
    await storeTekWrapped(tekWrapped)
  }
}
